import { RootState, useAppDispatch } from '_store';
import { pmvL10nArgs } from '_store/application/pmvLabels';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import pmvsReducer from '_store/pmv/pmvsReducers';
import { VmsStatusAlert } from '_store/pmv/pmvsTypes';
import { allVmsSelector, vmsStatusAlertsSelector } from '_store/pmv/selectors';
import { useLocalization } from '@fluent/react';
import { Box, Button, IconButton, Paper, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { toLatLng } from '../../routes/MapView/utils';

const titleKeyForKind = (kind: VmsStatusAlert['kind']): string =>
  kind === 'fault' ? 'forms-pmv-status-fault' : 'forms-pmv-status-recovered';

const PmvStatusNotification: FC = () => {
  const dispatch = useAppDispatch();
  const { l10n } = useLocalization();
  const statusAlerts = useSelector(vmsStatusAlertsSelector);
  const vmsList = useSelector(allVmsSelector);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);

  if (statusAlerts.length === 0) {
    return null;
  }

  const dismissAlert = (alertId: string) => {
    dispatch(pmvsReducer.actions.dismissVmsStatusAlert(alertId));
  };

  const openPmvDetails = (alert: VmsStatusAlert) => {
    const vms = vmsList.find((item) => item.id === alert.vmsId);
    if (vms?.latitude && vms?.longitude && mapRef) {
      mapRef.flyTo(toLatLng([vms.longitude, vms.latitude]), 15, {
        animate: true,
        duration: 1,
      });
    }

    setTimeout(() => {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.SINGLE_PMV,
          id: alert.vmsId,
        }),
      );
      dismissAlert(alert.id);
    }, 1000);
  };

  return (
    <>
      {statusAlerts.map((alert) => (
        <Paper
          key={alert.id}
          elevation={4}
          style={{
            width: 360,
            maxWidth: 'calc(100vw - 32px)',
            padding: '4px 4px 4px 10px',
          }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <Box flex={1} minWidth={0}>
              <Typography variant="body2" style={{ fontWeight: 500, lineHeight: 1.2 }}>
                {l10n.getString(titleKeyForKind(alert.kind), pmvL10nArgs({ name: alert.name }))}
              </Typography>
              <Typography
                variant="caption"
                style={{ opacity: 0.75, lineHeight: 1.15, display: 'block' }}
              >
                {`${alert.vmsId} · ${new Date(alert.createdAt).toLocaleString()}`}
              </Typography>
            </Box>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => openPmvDetails(alert)}
              style={{
                padding: '0 6px',
                minHeight: 24,
                minWidth: 0,
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              {l10n.getString('forms-pmv-status-alert-open')}
            </Button>
            <IconButton
              size="small"
              onClick={() => dismissAlert(alert.id)}
              style={{ padding: 2, flexShrink: 0 }}
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </>
  );
};

export default React.memo(PmvStatusNotification);
